import { SecureStoragePlugin } from 'capacitor-secure-storage-plugin';
import CryptoJS from 'crypto-js';

export const useTokenStore = defineStore('tokenStore', () => {

  const tokenKey = 'authToken';
  const tokenExpKey = 'authTokenExpiration';
  const refreshTokenKey = 'refreshToken';
  const refreshTokenExpKey = 'refreshTokenExpiration';
  const usernameKey = 'username';
  const siteNameKey = 'siteName';
  const passwordKey = 'password';
  const passwordSecretKey = 'passwordKey';
  const allTokenKeys = [tokenKey, tokenExpKey, refreshTokenKey, refreshTokenExpKey];

  const storedAccount = ref<StoredAccount[]>([]);

  async function deleteAccount(username: string, siteName: string) {
    const accKey = accountKey(siteName, username);
    const passKey = accountPasswordKey(siteName, username);

    // Remove the stored account and password key
    await remove(accKey);
    await remove(passKey);

    const accounts = JSON.parse((await get('allAccounts')) || '[]');
    const updatedAccounts = accounts.filter((key: string) => key !== accKey);
    await set('allAccounts', JSON.stringify(updatedAccounts));

    const lastAccount = await get('lastAccount');

    if (lastAccount === accKey) {
      if (updatedAccounts.length > 0) {
        await set('lastAccount', updatedAccounts[0]);
      } else {
        await remove('lastAccount'); 
      }
    }
  }

  async function saveAccountsOrder(accounts: StoredAccount[]) {
    const accountKeys = accounts.map(acc => accountKey(acc.siteName, acc.username));
    await set('allAccounts', JSON.stringify(accountKeys));
  }

  function accountKey(siteName: string, username: string) {
    return `account:${siteName.toLowerCase()}_${username.toLowerCase()}`;
  }

  function accountPasswordKey(siteName: string, username: string) {
    return `accountKey:${siteName.toLowerCase()}_${username.toLowerCase()}`;
  }

  async function getPasswordKeyForAccount(username: string, siteName: string): Promise<string | null> {
    const key = accountPasswordKey(siteName, username);

    return await get(key);
  }

  async function saveAccount(username: string, siteName: string, password: string) {
    const accKey = accountKey(siteName, username);
    const passKey = generatedKey();

    const encryptedPassword = encryptPassword(password, passKey);

    const account: StoredAccount = {
      username,
      siteName,
      password: encryptedPassword
    };

    const tasks = [
      set(accKey, JSON.stringify(account)),
      set(accountPasswordKey(siteName, username), passKey),
      set('lastAccount', accKey)
    ];

    const accounts = JSON.parse((await get('allAccounts')) || '[]');

    if (!accounts.includes(accKey)) {
      accounts.push(accKey);
      await set('allAccounts', JSON.stringify(accounts));
    }

    await Promise.all(tasks);
  }

  async function getLastUsedAccount(): Promise<StoredAccount | null> {
    const accKey = await get('lastAccount');

    if (!accKey) return null;

    const stored = await get(accKey);

    return stored ? JSON.parse(stored) : null;
  }

  async function getAllAccounts() {
    const accounts = JSON.parse((await get('allAccounts')) || '[]');
    const results: StoredAccount[] = [];

    for (const accKey of accounts) {
      const stored = await get(accKey);

      if (stored) results.push(JSON.parse(stored));
    }

    storedAccount.value= results;
  }

  /**
   * Retrieves and validates the access token from secure storage.
   *
   * If the token is expired, it will attempt to refresh it.
   * @returns An object containing the token and its expiration date,
   *          or null if the token is expired or not found.
   */
  async function getAccessToken() {
    const [token, expiration] = await Promise.all([get(tokenKey), get(tokenExpKey)]);

    const tokenObject = { token, expiration };

    if (isValidToken(tokenObject) === false) {
      return await refreshAccessToken();
    }

    return tokenObject;
  }

  /**
   * Retrieves and validates the refresh token from secure storage.
   * @returns An object containing the token and its expiration date,
   *          or null if the token is expired or not found.
   */
  async function getRefreshToken() {
    const [token, expiration] = await Promise.all([get(refreshTokenKey), get(refreshTokenExpKey)]);

    const tokenObject = { token, expiration };

    if (isValidToken(tokenObject) === false) {
      return null;
    }

    return tokenObject;
  }

  function encryptPassword(password: string, key: string): string {
    return CryptoJS.AES.encrypt(password, key).toString();
  }

  function decryptPassword(encryptedPassword: string, key: string): string {
    const bytes = CryptoJS.AES.decrypt(encryptedPassword, key);

    return bytes.toString(CryptoJS.enc.Utf8);
  }
  
  function generatedKey() {
    return CryptoJS.lib.WordArray.random(32).toString();
  }

  async function getUserCredentials() {
    const [username, siteName] = await Promise.all([get(usernameKey), get(siteNameKey)]);
  
    if (username === null || siteName === null) {
      return null;
    }
  
    const userCredentials = {
      username,
      siteName
    };
  
    return userCredentials;
  }
  
  async function getPasswordKey() {
    const passwordKey = await get(passwordSecretKey);
    
    if (passwordKey === null) {
      return null;
    }

    return passwordKey;
  }

  async function getPassword() {
    const password = await get(passwordKey);
  
    if (password === null) {
      return null;
    }

    return password;
  }

  async function hasValidAccessToken() {
    const [token, expiration] = await Promise.all([get(tokenKey), get(tokenExpKey)]);

    return isValidToken({ token, expiration });
  }

  async function hasValidRefreshToken() {
    const [token, expiration] = await Promise.all([get(refreshTokenKey), get(refreshTokenExpKey)]);

    return isValidToken({ token, expiration });
  }

  function isValidToken(tokenObject: { token: string | null, expiration: string | null }): tokenObject is { token: string, expiration: string } {
    const expirationDate = new Date(tokenObject.expiration ?? 0);

    const utcNow = new Date();

    return tokenObject.token !== null && tokenObject.expiration !== null && expirationDate > utcNow;
  }

  async function setAccessToken(token: string, expirationDate: string) {
    await set(tokenKey, token);
    await set(tokenExpKey, expirationDate);
  }

  async function setRefreshToken(token: string, expirationDate: string) {
    await set(refreshTokenKey, token);
    await set(refreshTokenExpKey, expirationDate);
  }

  async function get(key: string) {
    try {
      return (await SecureStoragePlugin.get({ key })).value;
    } catch {
      return null;
    }
  }

  async function set(key: string, value: string) {
    await SecureStoragePlugin.set({ key, value });
  }

  async function remove(key: string) {
    await SecureStoragePlugin.remove({ key });
  }

  async function $reset() {
    await Promise.all(allTokenKeys.map(key => SecureStoragePlugin.remove({ key })));
  }

  const LoginApi = useLoginAPI();
  const platformStore = usePlatformStore();

  async function refreshAccessToken() {
    const refreshToken = await getRefreshToken();

    if (!refreshToken) {
      useTokenStore().$reset();
      location.replace('/');

      return { token: '', expiration: 0 };
    }

    const softwareVersion = await platformStore.getSoftwareVersion();
    
    const refreshAccessTokenResponse = await LoginApi.refreshAccessToken(
      { RefreshToken: refreshToken.token }, 
      softwareVersion
    );

    if (refreshAccessTokenResponse === undefined || refreshAccessTokenResponse.Data === undefined) {
      useTokenStore().$reset();
      location.replace('/');

      return { token: '', expiration: 0 };
    }

    await setAccessToken(
      refreshAccessTokenResponse.Data.Tokens.AccessToken, 
      refreshAccessTokenResponse.Data.AccessTokenExp
    );

    return { 
      token: refreshAccessTokenResponse.Data.Tokens.AccessToken, 
      expiration: refreshAccessTokenResponse.Data.AccessTokenExp 
    };
  }

  async function getUserID(): Promise<string> {
    // Get user ID from token
    const data = await getAccessToken();
    
    // Decode token from base64
    const base64Url = data.token.split('.')[1];
    const base64 = base64Url.replace('-', '+').replace('_', '/');
    const decodedToken = JSON.parse(window.atob(base64));

    return decodedToken.sub;
  }

  return {
    getPassword,
    generatedKey,
    encryptPassword,
    decryptPassword,
    getUserCredentials,
    getPasswordKey,
    getAccessToken,
    setAccessToken,
    getRefreshToken,
    setRefreshToken,
    hasValidAccessToken,
    hasValidRefreshToken,
    refreshAccessToken,
    $reset,
    getUserID,
    saveAccount,
    getLastUsedAccount,
    getPasswordKeyForAccount,
    getAllAccounts,
    deleteAccount,
    saveAccountsOrder,
    _storedAccount: storedAccount,
    storedAccount: readonly(storedAccount)
  };
});
